/**
 * Player profiles (game history, accounts Phase 3): your record, split into games with people and games with bots
 * (a bot in a seat for more than two hands), and who may see it.
 *
 * - Open your own from "My stats" on the home screen, or anyone's from their name in the lobby.
 * - The server decides what you may see: your own always; someone else's only when they chose "Everyone"
 *   or "Friends only" once you're friends. Your own also shows your friends (friends.ts).
 * - Mounted outside the redrawn screen, like the feedback box, and rebuilt only when what it shows changes.
 */

import type { PlayerStats, ProfileInfo, ProfileVisibility } from '@euchre/core';
import { h } from '../dom.js';
import { app, rerender, send } from '../state.js';
import { auth, isAdult } from './account.js';
import { friendButton, friendsKey, friendsSection, refreshFriends, safetyControls, safetyKey, yourDataKey, yourDataSection } from './friends.js';

export const profileView = {
  open: false,
  loading: false,
  data: null as ProfileInfo | null,
};

let el: HTMLElement | null = null;
let shownKey = '';

export function openProfile(playerId?: string): void {
  profileView.open = true;
  profileView.loading = true;
  profileView.data = null;
  send({ type: 'get_profile', ...(playerId ? { playerId } : {}) });
  if (!playerId && auth.account) refreshFriends();
  rerender();
}

export function closeProfile(): void {
  profileView.open = false;
  profileView.loading = false;
  rerender();
}

export function profileArrived(p: ProfileInfo): void {
  if (!profileView.open) return;
  profileView.loading = false;
  profileView.data = p;
}

/** The server refused (for example, the player left): stop showing "Loading…". */
export function profileFailed(): void {
  if (profileView.open && profileView.loading) profileView.open = false;
}

const CHOICES: { v: ProfileVisibility; label: string; help: string }[] = [
  { v: 'private', label: 'Only me', help: 'Nobody else sees your stats.' },
  { v: 'friends', label: 'Friends only', help: 'Only your friends can see them.' },
  { v: 'everyone', label: 'Everyone', help: 'Anyone at your table can see your stats and skill rating.' },
];

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '–');

function statsBlock(title: string, s: PlayerStats): HTMLElement {
  if (!s.games && !s.handsPlayed && !s.unfinished) {
    return h('section', { class: 'profile-block' }, h('h3', { text: title }), h('p', { class: 'profile-empty', text: 'No games yet.' }));
  }
  const rows: [string, string][] = [
    ['Games', `${s.games} (won ${s.wins}, lost ${s.losses})${s.unfinished ? `, plus ${s.unfinished} unfinished` : ''}`],
    ['Win rate', pct(s.wins, s.games)],
    ['Hands played', String(s.handsPlayed)],
    ['Tricks won', String(s.tricksWon)],
    ['Called trump', `${s.trumpCalls} (round 1: ${s.callsRound1}, round 2: ${s.callsRound2})`],
    ['Calls made', `${s.callsMade} of ${s.trumpCalls} (${pct(s.callsMade, s.trumpCalls)})`],
    ['Got euchred', String(s.callsEuchred)],
    ['Euchred the other team', String(s.euchresScored)],
    ['Marches (all 5 tricks)', String(s.marches)],
    ['Went alone', `${s.loners} (made ${s.lonersMade}, all 5 tricks ${s.lonerMarches})`],
  ];
  if (s.defendAloneTries) rows.push(['Defended alone', `${s.defendAloneTries} (euchred them ${s.defendAloneWins})`]);
  if (s.stuckAsDealer) rows.push(['Stuck as dealer', String(s.stuckAsDealer)]);
  return h(
    'section',
    { class: 'profile-block' },
    h('h3', { text: title }),
    h('dl', { class: 'profile-stats' }, ...rows.flatMap(([k, v]) => [h('dt', { text: k }), h('dd', { text: v })])),
  );
}

function privacyPicker(current: ProfileVisibility): HTMLElement {
  return h(
    'fieldset',
    { class: 'profile-privacy' },
    h('legend', { text: 'Who can see my stats' }),
    ...CHOICES.filter((c) => c.v !== 'everyone' || isAdult()).map((c) =>
      h(
        'label',
        { class: 'profile-choice' },
        h('input', {
          type: 'radio',
          name: 'profile-visibility',
          value: c.v,
          checked: c.v === current,
          disabled: app.conn !== 'open',
          onchange: () => {
            send({ type: 'set_profile_visibility', visibility: c.v });
          },
        }),
        h('span', {}, h('strong', { text: c.label }), h('span', { class: 'profile-help', text: ` ${c.help}` })),
      ),
    ),
  );
}

function body(): HTMLElement[] {
  const p = profileView.data;
  if (profileView.loading || !p) return [h('h2', { id: 'profile-title', text: 'Stats' }), h('p', { text: 'Loading…' })];
  const title = p.self ? 'My stats' : `${p.name}’s stats`;
  const out: HTMLElement[] = [h('h2', { id: 'profile-title', text: title })];
  const fb = p.self ? null : friendButton(p);
  if (fb) out.push(fb);
  if (!p.visible) {
    out.push(h('p', { text: `${p.name} keeps their stats private.` }));
    if (!p.self) out.push(safetyControls(p));
    return out;
  }
  if (p.self && !p.signedIn) {
    out.push(h('p', { class: 'profile-note', text: 'These stats belong to this browser. Use “Save my name” on the home screen to keep them on any device and choose who can see them.' }));
  }
  if (p.self && p.visibility) out.push(privacyPicker(p.visibility));
  if (p.self && p.signedIn) out.push(friendsSection());
  if (p.rating) {
    const r = p.rating;
    const text = r.games
      ? `${r.value}${r.provisional ? ` (still settling: ${r.games} of 10 rated games)` : ` after ${r.games} rated games`}`
      : p.self
        ? 'Starts at 1000 after your first finished game with four people.'
        : 'Not rated yet.';
    out.push(
      h('section', { class: 'profile-block' }, h('h3', { text: 'Skill rating' }), h('p', { class: 'profile-rating', text })),
    );
  }
  if (p.record) {
    out.push(statsBlock('Games with people', p.record.people), statsBlock('Games with bots', p.record.bots));
    out.push(
      h('p', {
        class: 'profile-note',
        text: p.self
          ? 'A game counts as a bot game when a bot plays any seat for more than two hands. Your hand stats only count hands you played yourself. Unfinished games count for hand stats, not wins. Your skill rating shows to others only if you choose Everyone.'
          : 'A game counts as a bot game when a bot plays any seat for more than two hands. Unfinished games count for hand stats, not wins.',
      }),
    );
  } else {
    out.push(h('p', { text: 'Stats aren’t available right now.' }));
  }
  if (!p.self) out.push(safetyControls(p));
  if (p.self && p.signedIn) out.push(yourDataSection());
  return out;
}

export function profileDialog(): HTMLElement | null {
  if (!profileView.open) {
    el = null;
    shownKey = '';
    return null;
  }
  const key = JSON.stringify([profileView.loading, profileView.data, app.conn, friendsKey(), safetyKey(), yourDataKey()]);
  if (el && key === shownKey) return el;
  shownKey = key;
  el = h(
    'div',
    {
      class: 'away-scrim profile-scrim',
      onclick: (ev: Event) => {
        if (ev.target === ev.currentTarget) closeProfile();
      },
      onkeydown: (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') closeProfile();
      },
    },
    h(
      'div',
      { class: 'away-card profile-card', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'profile-title' } },
      ...body(),
      h('div', { class: 'feedback-actions' }, h('button', { class: 'btn', type: 'button', text: 'Close', onclick: closeProfile })),
    ),
  );
  return el;
}
